import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuthStore, type UknUser } from '@/store/auth';
import { APP_BASE, API_BASE } from '@/lib/api-base';
import { ApiError, errorMessage } from '@/lib/api-client';

type ProvisionInfo = { username: string; email: string; name: string; role: string };
type ProvisionResponse = { access_token: string; user: UknUser };

/**
 * First-time SSO account setup. Reached when an AUTHORIZED uKnowva user
 * (their group is in sso_access_rules) has no payroll account yet. They
 * set a password here; the account is then created with the role mapped
 * from uKnowva. Guarded by the short-lived ?prov= token.
 */
export function SsoSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const prov = searchParams.get('prov');

  const [info, setInfo] = useState<ProvisionInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!prov) {
      setLoadError('This setup link is missing its token. Please start from uKnowva again.');
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/auth/sso/provision-info?prov=${encodeURIComponent(prov)}`)
      .then(async (res) => {
        if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
        return res.json();
      })
      .then((data: ProvisionInfo) => { if (!cancelled) setInfo(data); })
      .catch((err) => { if (!cancelled) setLoadError(errorMessage(err)); });
    return () => { cancelled = true; };
  }, [prov]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/sso/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: prov, password }),
      });
      if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => null));
      const data: ProvisionResponse = await res.json();
      setSession(data.access_token, data.user);
      navigate(`${APP_BASE}/`, { replace: true });
    } catch (err) {
      setFormError(errorMessage(err));
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                 justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
        <Box sx={{ maxWidth: 420, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
          <Button variant="outlined" onClick={() => navigate(`${APP_BASE}/login`, { replace: true })}>
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  if (!info) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
                 alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
        <CircularProgress size={32} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">Preparing your account…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
               justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }} elevation={3}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Create your payroll account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Welcome, {info.name || info.username}. Set a password to finish. You can then
          sign in via uKnowva or directly here.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {info.email && <Chip size="small" label={info.email} />}
          <Chip size="small" color="primary" label={`Role: ${info.role}`} />
        </Box>

        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

        <form onSubmit={onSubmit} noValidate>
          <TextField
            label="Password" type="password" fullWidth margin="normal"
            value={password} onChange={(e) => setPassword(e.target.value)}
            helperText="At least 8 characters" autoFocus
          />
          <TextField
            label="Confirm password" type="password" fullWidth margin="normal"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}
                  disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : 'Create account'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
